import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDesignMockup, DCSwatchFillMode } from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const needsMockupPolling = (mockup: DCDesignMockup) => {
  const status = mockup.status ?? "";
  const hasImage = Boolean(mockup.mockup_path || mockup.thumbnail_path);
  return (status === "pending" || status === "generating") && !hasImage;
};

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, {
    headers: { "X-Wallmates-Auth": authToken }
  });
  if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
  return data.data;
};

export const useDesignMockups = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [mockups, setMockups] = useState<DCDesignMockup[]>([]);
  const [loadingMockups, setLoadingMockups] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [noMockupsFound, setNoMockupsFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingTimeoutRef = useRef<number | null>(null);
  const pollingDesignIdRef = useRef<number | null>(null);
  const pollingInFlightRef = useRef(false);
  const pollingActiveRef = useRef(false);
  const pollingSkipDesignIdRef = useRef<number | null>(null);
  const emptyResponseDesignIdRef = useRef<number | null>(null);
  /** Always-current snapshot of `mockups` so generateMockupsBatch can deduplicate
   *  without having to be re-created on every poll tick. */
  const mockupsRef = useRef<DCDesignMockup[]>([]);
  useEffect(() => { mockupsRef.current = mockups; }, [mockups]);

  const fetchMockups = useCallback(async (designId: number, silent = false) => {
    if (!designId) return null;
    try {
      if (!silent) setLoadingMockups(true);
      const q = `query($design_id:ID!){ dcDesignMockups(design_id:$design_id){ id design_id mockup_template_id status thumbnail_path mockup_path status_reason manual_upload hidden shadow_opacity enhance_shadows created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { design_id: designId });
      const next = (data?.dcDesignMockups || []) as DCDesignMockup[];
      setMockups(next);
      if (!next.length) {
        setNoMockupsFound(true);
        emptyResponseDesignIdRef.current = designId;
      } else {
        setNoMockupsFound(false);
        emptyResponseDesignIdRef.current = null;
      }
      return next;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to load mockups." });
      return null;
    } finally {
      if (!silent) setLoadingMockups(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollingDesignIdRef.current = null;
    pollingInFlightRef.current = false;
    pollingActiveRef.current = false;
  }, []);

  const pollOnce = useCallback(async (designId: number) => {
    if (!designId || pollingInFlightRef.current) return;
    pollingInFlightRef.current = true;
    try {
      const next = await fetchMockups(designId, true);
      if (!next?.length) {
        pollingSkipDesignIdRef.current = designId;
        stopPolling();
        return;
      }
      const shouldContinue = next.some(needsMockupPolling);
      if (!shouldContinue) {
        pollingSkipDesignIdRef.current = designId;
        stopPolling();
        return;
      }
      pollingTimeoutRef.current = window.setTimeout(() => {
        if (pollingActiveRef.current && pollingDesignIdRef.current === designId) {
          pollOnce(designId);
        }
      }, 1000);
    } finally {
      pollingInFlightRef.current = false;
    }
  }, [fetchMockups, stopPolling]);

  const startPolling = useCallback((designId: number, options?: { force?: boolean }) => {
    if (!designId) return;
    if (!options?.force && pollingSkipDesignIdRef.current === designId) return;
    if (pollingActiveRef.current && pollingDesignIdRef.current === designId) return;
    stopPolling();
    pollingActiveRef.current = true;
    pollingDesignIdRef.current = designId;
    pollOnce(designId);
  }, [pollOnce, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const listMockups = useCallback(async (designId: number, options?: { force?: boolean }) => {
    if (!options?.force && emptyResponseDesignIdRef.current === designId) {
      return mockups;
    }
    if (options?.force) {
      pollingSkipDesignIdRef.current = null;
      emptyResponseDesignIdRef.current = null;
      setNoMockupsFound(false);
    }
    const next = await fetchMockups(designId, false);
    if (!next?.length) {
      pollingSkipDesignIdRef.current = designId;
      stopPolling();
      return next;
    }
    const shouldContinue = next.some(needsMockupPolling);
    if (shouldContinue) {
      startPolling(designId);
    } else {
      pollingSkipDesignIdRef.current = designId;
      stopPolling();
    }
    return next;
  }, [fetchMockups, startPolling, stopPolling]);

  const generateMockup = useCallback(async (designId: number, templateId: number) => {
    if (!designId || !templateId) return null;
    try {
      setGenerating(true);
      setError(null);
      setNoMockupsFound(false);
      emptyResponseDesignIdRef.current = null;
      const mutation = `mutation($input:DCDesignMockupGenerateInput!){ generateDCDesignMockup(input:$input){ id design_id mockup_template_id status thumbnail_path mockup_path status_reason manual_upload hidden shadow_opacity enhance_shadows created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, {
        input: { design_id: designId, mockup_template_id: templateId }
      });
      const created = data?.generateDCDesignMockup as DCDesignMockup | undefined;
      if (created?.id) {
        pollingSkipDesignIdRef.current = null;
        startPolling(designId, { force: true });
        setMockups((prev) => [created, ...prev.filter((mockup) => mockup.id !== created.id)]);
      }
      return created || null;
    } catch (err: any) {
      setError(err?.message || "Mockup generation failed.");
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Mockup generation failed." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar, startPolling]);

  const retryMockup = useCallback(async (
    designId: number,
    mockupId: number,
    overrides?: { shadow_opacity?: number; enhance_shadows?: boolean }
  ) => {
    if (!designId || !mockupId) return null;
    try {
      setGenerating(true);
      setError(null);
      setNoMockupsFound(false);
      emptyResponseDesignIdRef.current = null;
      const mutation = `mutation($id:ID!,$shadow_opacity:Int,$enhance_shadows:Boolean){ retryDCDesignMockup(id:$id, shadow_opacity:$shadow_opacity, enhance_shadows:$enhance_shadows){ id design_id mockup_template_id status thumbnail_path mockup_path status_reason manual_upload hidden shadow_opacity enhance_shadows created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, {
        id: mockupId,
        shadow_opacity: overrides?.shadow_opacity ?? null,
        enhance_shadows: overrides?.enhance_shadows ?? null
      });
      const updated = data?.retryDCDesignMockup as DCDesignMockup | undefined;
      if (updated?.id) {
        pollingSkipDesignIdRef.current = null;
        startPolling(designId, { force: true });
        setMockups((prev) => prev.map((mockup) => (mockup.id === updated.id ? { ...mockup, ...updated } : mockup)));
      }
      return updated || null;
    } catch (err: any) {
      setError(err?.message || "Mockup retry failed.");
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Mockup retry failed." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar, startPolling]);

  const toggleMockupVisibility = useCallback(async (mockupId: number) => {
    if (!mockupId) return null;
    try {
      const mutation = `mutation($id:ID!){ toggleDCDesignMockupVisibility(id:$id){ id design_id mockup_template_id status thumbnail_path mockup_path status_reason manual_upload hidden shadow_opacity enhance_shadows created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, { id: mockupId });
      const updated = data?.toggleDCDesignMockupVisibility as DCDesignMockup | undefined;
      if (updated?.id) {
        setMockups((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
      }
      return updated || null;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to toggle mockup visibility." });
      return null;
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const generateMockupsBatch = useCallback(async (designId: number, templateIds: number[]) => {
    if (!designId || !templateIds.length) return null;

    // Per-template dispatch based on the latest mockup row this design has for it.
    // This prevents duplicate-row spam regardless of where the batch came from
    // (preset apply, modify flow, manual Generate, etc):
    //  - pending/generating: skip; tell the user it's already in flight
    //  - failed: retry the existing row in place (no new row)
    //  - completed or no row: queue as a fresh generation (creates a new row)
    const latestByTemplate = new Map<number, DCDesignMockup>();
    for (const m of mockupsRef.current) {
      if (m.mockup_template_id == null) continue;
      const tid = Number(m.mockup_template_id);
      const existing = latestByTemplate.get(tid);
      const ts = m.created_at ? new Date(m.created_at).getTime() : 0;
      const existingTs = existing?.created_at ? new Date(existing.created_at).getTime() : 0;
      if (!existing || ts > existingTs) latestByTemplate.set(tid, m);
    }

    const toQueue: number[] = [];
    const toRetry: { templateId: number; rowId: number }[] = [];
    const skippedInFlight: number[] = [];
    for (const tid of templateIds) {
      const latest = latestByTemplate.get(tid);
      const status = latest?.status;
      if (latest && (status === "pending" || status === "generating")) {
        skippedInFlight.push(tid);
      } else if (latest && status === "failed" && latest.id) {
        toRetry.push({ templateId: tid, rowId: latest.id });
      } else {
        toQueue.push(tid);
      }
    }

    if (toQueue.length === 0 && toRetry.length === 0) {
      // Everything was already in-flight — show the friendly message and bail.
      setSnackbar({
        show: true,
        snackbarLevel: "info",
        text: "These mockups are already generating. Please wait. If the problem persists, contact us for more help.",
      });
      return null;
    }
    if (skippedInFlight.length > 0) {
      setSnackbar({
        show: true,
        snackbarLevel: "info",
        text: `${skippedInFlight.length} mockup${skippedInFlight.length === 1 ? " is" : "s are"} already generating — handling the remaining ${toQueue.length + toRetry.length}.`,
      });
    }

    try {
      setGenerating(true);
      setError(null);

      // Retry previously-failed rows in place. Done in parallel to avoid serial latency,
      // and we don't await each result because retryMockup already handles errors per-row.
      if (toRetry.length > 0) {
        await Promise.all(toRetry.map((item) => retryMockup(designId, item.rowId)));
      }

      // Queue truly-new templates as fresh rows.
      if (toQueue.length === 0) {
        // Pure retry case — return a synthetic queued response so callers' optimistic
        // token deductions and snackbars still fire as expected.
        return { status: "queued", mockups: [] } as { status: string; mockups: DCDesignMockup[] };
      }
      const mutation = `mutation($input:DCDesignMockupsBatchInput!){ generateDCDesignMockupsBatch(input:$input){ status mockups { id design_id mockup_template_id status thumbnail_path mockup_path status_reason manual_upload hidden shadow_opacity enhance_shadows created_at updated_at } } }`;
      const data = await graphqlRequest(authToken, mutation, {
        input: { design_id: designId, mockup_template_ids: toQueue }
      });
      return data?.generateDCDesignMockupsBatch || null;
    } catch (err: any) {
      setError(err?.message || "Mockup batch generation failed.");
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Mockup batch generation failed." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar, retryMockup]);

  /**
   * Re-runs 16" x 16" swatch generation for a design, optionally overriding how
   * non-square artwork is squared up. The backend soft-deletes the old swatch row and
   * creates a fresh `generating` one, so kick polling to pick the new image up.
   */
  const regenerateSwatch = useCallback(async (designId: number, fillMode?: DCSwatchFillMode) => {
    if (!designId) return false;
    try {
      setGenerating(true);
      setError(null);
      setNoMockupsFound(false);
      emptyResponseDesignIdRef.current = null;
      const mutation = `mutation($design_id:ID!,$fill_mode:String){ regenerateDCSwatch(design_id:$design_id, fill_mode:$fill_mode) }`;
      await graphqlRequest(authToken, mutation, { design_id: designId, fill_mode: fillMode ?? null });
      pollingSkipDesignIdRef.current = null;
      await fetchMockups(designId, true);
      startPolling(designId, { force: true });
      setSnackbar({ show: true, snackbarLevel: "success", text: "Regenerating swatch…" });
      return true;
    } catch (err: any) {
      setError(err?.message || "Swatch regeneration failed.");
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Swatch regeneration failed." });
      return false;
    } finally {
      setGenerating(false);
    }
  }, [authToken, fetchMockups, loginFunctions.logout, setSnackbar, startPolling]);

  return {
    mockups,
    loadingMockups,
    generating,
    noMockupsFound,
    error,
    listMockups,
    startPolling,
    stopPolling,
    generateMockup,
    retryMockup,
    generateMockupsBatch,
    toggleMockupVisibility,
    regenerateSwatch
  };
};
